"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import Button from "../../../../../components/Button";
import FormInput from "../../../../../components/FormInput";
import Checkbox from "../../../../../components/Checkbox";
import { Check } from "lucide-react";

import { useConsultation } from "../../../../../context/ConsultationContext";
import { validateDateOfBirth, validateSouthAfricanContact } from "../../utils/validation";

import styles from "./css/InjectionAdministration.module.css";

const initialForm = {
  patientFullName: "",
  patientDob: "",
  patientContactNumber: "",
  dateOfScreening: "",

  patientSignature: "",
  patientSignatureDate: "",

  pharmacistName: "",
  pharmacistSapcRegistrationNumber: "",
  pharmacistSignature: "",
  pharmacistSignatureDate: "",

  followUpConsent: false,
};

export default function CardiometabolicScreening() {
  const navigate = useNavigate();
  const location = useLocation();
  const { consultationData, setIsInConsultation, getStepDraft, setStepDraft } = useConsultation();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayStr = useMemo(() => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(today.getDate())}-${pad(today.getMonth() + 1)}-${today.getFullYear()}`;
  }, [today]);

  const patient = consultationData?.patient || {};
  const patientName = useMemo(() => {
    const first = String(patient?.firstname || "").trim();
    const last = String(patient?.surname || "").trim();
    const full = `${first} ${last}`.trim();
    return full;
  }, [patient?.firstname, patient?.surname]);

  const [formData, setFormData] = useState(() => ({
    ...initialForm,
    patientFullName: patientName || initialForm.patientFullName,
    dateOfScreening: todayStr,
    patientSignatureDate: todayStr,
    pharmacistSignatureDate: todayStr,
  }));

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === "patientDob") {
      const nextValue = String(value || "").trim();
      if (!nextValue) {
        setValidationErrors((prev) => {
          if (!prev.patientDob) return prev;
          const next = { ...prev };
          delete next.patientDob;
          return next;
        });
        return;
      }

      const dobValidation = validateDateOfBirth(nextValue);
      setValidationErrors((prev) => {
        if (!dobValidation.isValid) {
          return { ...prev, patientDob: dobValidation.error };
        }
        if (!prev.patientDob) return prev;
        const next = { ...prev };
        delete next.patientDob;
        return next;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    // Validate patient full name
    if (!formData.patientFullName.trim()) {
      errors.patientFullName = "Patient name is required";
    }

    // Validate patient DOB
    const dobValidation = validateDateOfBirth(formData.patientDob);
    if (!dobValidation.isValid) {
      errors.patientDob = dobValidation.error;
    }

    // Validate contact number
    const contactValidation = validateSouthAfricanContact(formData.patientContactNumber);
    if (!contactValidation.isValid) {
      errors.patientContactNumber = contactValidation.error;
    }

    // Validate date of screening
    if (!formData.dateOfScreening) {
      errors.dateOfScreening = "Date of screening is required";
    }

    // Validate patient signature
    if (!formData.patientSignature.trim()) {
      errors.patientSignature = "Patient signature is required";
    }

    // Validate patient signature date
    if (!formData.patientSignatureDate) {
      errors.patientSignatureDate = "Patient signature date is required";
    }

    // Validate pharmacist name
    if (!formData.pharmacistName.trim()) {
      errors.pharmacistName = "Pharmacist name is required";
    }

    // Validate SAPC registration number
    if (!formData.pharmacistSapcRegistrationNumber.trim()) {
      errors.pharmacistSapcRegistrationNumber = "SAPC registration number is required";
    }

    // Validate pharmacist signature
    if (!formData.pharmacistSignature.trim()) {
      errors.pharmacistSignature = "Pharmacist signature is required";
    }

    // Validate pharmacist signature date
    if (!formData.pharmacistSignatureDate) {
      errors.pharmacistSignatureDate = "Pharmacist signature date is required";
    }

    return errors;
  };

  const focusFirstError = (errors) => {
    const fieldOrder = [
      "patientFullName",
      "patientDob",
      "patientContactNumber",
      "dateOfScreening",
      "patientSignature",
      "pharmacistName",
      "pharmacistSapcRegistrationNumber",
      "pharmacistSignature",
    ];

    const field = fieldOrder.find((f) => errors?.[f]);
    if (!field) return;

    const el = document.getElementById(field) || document.querySelector(`[name="${field}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (el?.focus) el.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setTimeout(() => focusFirstError(errors), 0);
      return;
    }

    setSaveError("");
    setSaving(true);
    try {
      const payload = {
        ...formData,
        appointmentId: consultationData?.appointmentId ?? null,
        patientId: consultationData?.patientId ?? null,
      };

      // Save to consultation draft context (for DB save on Invoice Finalize)
      const existing = getStepDraft?.("consentForms", {}) || {};
      setStepDraft?.("consentForms", {
        ...existing,
        cardiometabolic_screening: {
          answers: payload,
          patientSigned: !!formData.patientSignature,
          patientSignedAt: formData.patientSignatureDate ?? null,
          practitionerSigned: !!formData.pharmacistSignature,
          practitionerSignedAt: formData.pharmacistSignatureDate ?? null,
        },
      });

      try {
        sessionStorage.setItem("clinic.consent.cardiometabolicScreening", JSON.stringify(payload));
      } catch {}

      const continueTo = location?.state?.continueTo;
      setIsInConsultation?.(true);
      navigate(typeof continueTo === "string" && continueTo.trim() ? continueTo : "/customer/clinic/patient-overview");
    } catch (err) {
      setSaveError(err?.message || "Failed to save consent form.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <form className={styles.formContainer} onSubmit={handleSubmit}>
            <header className={styles.docHeader}>
              <div className={styles.docHeaderTop}>
                <div className={styles.pharmacyName}>Durban North Pharmacy</div>
                <div className={styles.pharmacyContact}>35 Umhlanga Rocks Drive | Tel: 031 563 7577</div>
              </div>
              <div className={styles.purpleRule} />
              <h1 className={styles.docTitle}>Cardiometabolic Screening — Short-Form Informed Consent</h1>
            </header>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>Patient Information</h3>
              <div className={styles.grid2}>
                <div className={styles.inputGroup}>
                  <label htmlFor="patientFullName">Patient Name</label>
                  <input
                    id="patientFullName"
                    name="patientFullName"
                    value={formData.patientFullName}
                    onChange={handleInputChange}
                    placeholder="Patient name"
                    autoComplete="name"
                    className={validationErrors.patientFullName ? styles.inputError : ""}
                  />
                  {validationErrors.patientFullName && (
                    <div className={styles.errorMessage}>{validationErrors.patientFullName}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="patientDob">DOB</label>
                  <input
                    id="patientDob"
                    name="patientDob"
                    value={formData.patientDob}
                    onChange={handleInputChange}
                    placeholder="DD-MM-YYYY"
                    className={validationErrors.patientDob ? styles.inputError : ""}
                  />
                  {validationErrors.patientDob && (
                    <div className={styles.errorMessage}>{validationErrors.patientDob}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="patientContactNumber">Contact Number</label>
                  <input
                    id="patientContactNumber"
                    name="patientContactNumber"
                    value={formData.patientContactNumber}
                    onChange={handleInputChange}
                    placeholder="Contact number"
                    autoComplete="tel"
                    className={validationErrors.patientContactNumber ? styles.inputError : ""}
                  />
                  {validationErrors.patientContactNumber && (
                    <div className={styles.errorMessage}>{validationErrors.patientContactNumber}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date of Screening"
                    type="text"
                    name="dateOfScreening"
                    value={formData.dateOfScreening}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                    inputClassName={validationErrors.dateOfScreening ? styles.inputError : ""}
                  />
                  {validationErrors.dateOfScreening && (
                    <div className={styles.errorMessage}>{validationErrors.dateOfScreening}</div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>Consent</h3>
              <div className={styles.consentTextBlock}>
                <p className={styles.statement}>
                  I understand that this cardiometabolic screening is a pharmacy-based health screening service intended to assess selected health
                  indicators, which may include blood pressure, blood glucose, cholesterol (if applicable), weight, waist circumference, and BMI.
                </p>
                <p className={styles.statement}>
                  I acknowledge that this service is preventative and advisory in nature, does not constitute a medical diagnosis, does not replace
                  consultation with a medical practitioner, and reflects a point-in-time assessment. Advice provided is within the pharmacist’s
                  professional scope of practice and referral may be advised if results are abnormal.
                </p>
                <p className={styles.statement}>
                  I understand that finger-prick testing (where applicable) may cause minor discomfort, participation is voluntary, I may withdraw at
                  any time, and my personal information will be kept confidential in accordance with legal and professional requirements.
                </p>
              </div>
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>Signatures</h3>
              <div className={styles.grid2}>
                <div className={styles.inputGroup}>
                  <label htmlFor="patientSignature">Patient Signature</label>
                  <input
                    id="patientSignature"
                    name="patientSignature"
                    value={formData.patientSignature}
                    onChange={handleInputChange}
                    placeholder="Type name as signature"
                    className={validationErrors.patientSignature ? styles.inputError : ""}
                  />
                  {validationErrors.patientSignature && (
                    <div className={styles.errorMessage}>{validationErrors.patientSignature}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date"
                    type="text"
                    name="patientSignatureDate"
                    value={formData.patientSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                  {validationErrors.patientSignatureDate && (
                    <div className={styles.errorMessage}>{validationErrors.patientSignatureDate}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="pharmacistName">Pharmacist Name</label>
                  <input
                    id="pharmacistName"
                    name="pharmacistName"
                    value={formData.pharmacistName}
                    onChange={handleInputChange}
                    placeholder="Pharmacist name"
                    className={validationErrors.pharmacistName ? styles.inputError : ""}
                  />
                  {validationErrors.pharmacistName && (
                    <div className={styles.errorMessage}>{validationErrors.pharmacistName}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="pharmacistSapcRegistrationNumber">SAPC Registration Number</label>
                  <input
                    id="pharmacistSapcRegistrationNumber"
                    name="pharmacistSapcRegistrationNumber"
                    value={formData.pharmacistSapcRegistrationNumber}
                    onChange={handleInputChange}
                    placeholder="SAPC registration number"
                    className={validationErrors.pharmacistSapcRegistrationNumber ? styles.inputError : ""}
                  />
                  {validationErrors.pharmacistSapcRegistrationNumber && (
                    <div className={styles.errorMessage}>{validationErrors.pharmacistSapcRegistrationNumber}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="pharmacistSignature">Pharmacist Signature</label>
                  <input
                    id="pharmacistSignature"
                    name="pharmacistSignature"
                    value={formData.pharmacistSignature}
                    onChange={handleInputChange}
                    placeholder="Type name as signature"
                    className={validationErrors.pharmacistSignature ? styles.inputError : ""}
                  />
                  {validationErrors.pharmacistSignature && (
                    <div className={styles.errorMessage}>{validationErrors.pharmacistSignature}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <FormInput
                    label="Date"
                    type="text"
                    name="pharmacistSignatureDate"
                    value={formData.pharmacistSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                  {validationErrors.pharmacistSignatureDate && (
                    <div className={styles.errorMessage}>{validationErrors.pharmacistSignatureDate}</div>
                  )}
                </div>
              </div>

              <div className={styles.checkboxRow}>
                <Checkbox
                  id="followUpConsent"
                  label="I consent to being contacted for follow-up or reminders related to this service"
                  checked={formData.followUpConsent}
                  onChange={(e) => setFormData((p) => ({ ...p, followUpConsent: e.target.checked }))}
                />
              </div>
            </div>

            {saveError && <div className={styles.inlineError}>{saveError}</div>}

            <div className={styles.completeContainer}>
              <Button variant="primary" className={styles.completeButton} disabled={saving} type="submit">
                {saving ? (
                  "Completing..."
                ) : (
                  <>
                    Complete <Check size={22} />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}